import { Component, HostListener, Input, inject } from '@angular/core';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { BehaviorSubject, defer, Observable, of } from 'rxjs';

import { DATA_SELECTOR_INJECTION_TOKEN, emitValueIfValid, Progress } from '@aiwyn/shared';
import {
  BaseCommonClientRequest,
  ClientRequestIdentityHelper,
  ClientRequestStatusPill,
  CommonClientRequestDocumentUpload,
  RequestIdentityType,
  TypedIdentity,
} from '@aiwyn/common/client-requests';
import { DatePipe } from '@angular/common';
import { RejectedClientRequestItem, ClientRequestItemStatus } from '@aiwyn/api';
import { ClientRequestsState, SetSelectedClientRequestId } from '../../state';
import { Store } from '@ngxs/store';
import { ClientRequestsLayoutService } from '../../services';
import { openFieldGuideRequest, ClientRequestWithFieldGuide, isFieldGuideRequest, getFieldGuideDeepLink } from '../../../../common/client-requests/utility/fieldguide.utils';
import { SnackbarService } from '../../../../shared/components/snackbar/snackbar.service';
import { SnackbarType } from '../../../../shared/components/snackbar/snackbar.models';

@Component({
  selector: 'app-client-request',
  templateUrl: './client-request.component.html',
  styleUrls: ['./client-request.component.scss', '../../styles/client-requests.scss'],
  providers: [
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'client-request',
    },
  ],
  standalone: false,
})
export class ClientRequestComponent {
  @Input() instance!: BaseCommonClientRequest;

  protected showRejectedDetails = false;
  public dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);

  isGroupSelected$ = new BehaviorSubject<boolean>(undefined);

  @Input() set isGroupSelected(isGroupSelected: boolean) {
    emitValueIfValid(isGroupSelected, this.isGroupSelected$);
  }

  rejectedItems$: Observable<RejectedClientRequestItem[]> = defer(() => this.instance.rejectedItems$).pipe(
    map(rejectedItems => {
      return rejectedItems;
    }),
    shareReplay(1),
  );

  statusPill$: Observable<ClientRequestStatusPill> = defer(() => this.instance.statusPill$).pipe(
    shareReplay(1),
  );

  progress$: Observable<Progress> = defer(() => this.instance.typeClass$).pipe(
    switchMap(typeClass => typeClass instanceof CommonClientRequestDocumentUpload ? typeClass.statusDetails$ : of(undefined)),
  );

  // Item status counts observable - calculate from requestItems
  itemStatusCounts$ = defer(() => this.instance.requestItems$).pipe(
    map(requestItems => {
      return {
        open: requestItems.filter(item => item.status === ClientRequestItemStatus.OPEN).length,
        inReview: requestItems.filter(item => item.status === ClientRequestItemStatus.IN_REVIEW).length,
        rejected: requestItems.filter(item => item.status === ClientRequestItemStatus.REJECTED).length,
        approved: requestItems.filter(item => item.status === ClientRequestItemStatus.DONE).length,
      };
    }),
    shareReplay(1)
  );

  // Overdue count observable - calculate from requestItems
  overdueCount$ = defer(() => this.instance.requestItems$).pipe(
    map(requestItems => {
      if (requestItems.length === 0) {
        return 0;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to start of today for accurate comparison

      let overdueCount = 0;
      
      requestItems.forEach(item => {
        // Only count non-completed items
        if (item.status !== ClientRequestItemStatus.DONE && item.dueDate) {
          const dueDate = new Date(item.dueDate);
          dueDate.setHours(0, 0, 0, 0); // Set to start of due date
          
          if (dueDate < today) {
            overdueCount++;
          }
        }
      });

      return overdueCount;
    }),
    shareReplay(1)
  );

  // Conditional avatars observable - use client-level avatars for FieldGuide requests, request-item avatars for others
  requestItemsAvatars$ = defer(() => 
    this.isFieldGuideRequest$.pipe(
      take(1),
      switchMap(isFieldGuide => {
        if (isFieldGuide) {
          return this.instance.avatars$;
        } else {
          return this.instance.requestItemsAvatars$;
        }
      })
    )
  ).pipe(
    shareReplay(1)
  );

  // FieldGuide detection and deep link observables - using model$ directly
  isFieldGuideRequest$ = defer(() => this.instance.model$).pipe(
    map(({ data }) => isFieldGuideRequest(data as ClientRequestWithFieldGuide)),
    shareReplay(1)
  );

  fieldGuideDeepLink$ = defer(() => this.instance.model$).pipe(
    map(({ data }) => getFieldGuideDeepLink(data as ClientRequestWithFieldGuide)),
    shareReplay(1)
  );

  // FieldGuide-aware name observable
  displayName$ = defer(() => this.instance.model$).pipe(
    map(({ data }) => {
      if (isFieldGuideRequest(data as ClientRequestWithFieldGuide)) {
        const currentName = data?.name || '';
        if (!currentName.includes('Request List')) {
          return `${currentName} Request List`;
        }
        return currentName;
      }
      return data?.name;
    }),
    shareReplay(1)
  );

  constructor(
    private readonly datePipe: DatePipe,
    private readonly store: Store,
    private readonly layoutService: ClientRequestsLayoutService,
    private readonly snackbarService: SnackbarService,
  ) {}

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    event.stopPropagation();
    
    // Prevent navigation for FieldGuide requests
    this.isFieldGuideRequest$.pipe(take(1)).subscribe(isFieldGuide => {
      if (isFieldGuide) {
        // Show toast message for FieldGuide requests
        this.snackbarService.open(
          'To view request details for Fieldguide projects, please use Fieldguide directly by clicking on "View" button',
          SnackbarType.INFO
        );
        return;
      }
      
      this.onRequestSelected(this.instance.identity);
    });
  }
  onRequestSelected(requestIdentity: TypedIdentity<RequestIdentityType>): void {
    const selectedRequestId = this.store.selectSnapshot(ClientRequestsState.getSlices.selectedRequestId);
    if (selectedRequestId) {
      return;
    }

    const requestId = ClientRequestIdentityHelper.parseRequestIdentity(requestIdentity).id;
    this.store.dispatch(new SetSelectedClientRequestId(requestId));
  }

  toggleRejectedDetails(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.showRejectedDetails = !this.showRejectedDetails;
  }

  onViewInFieldGuide(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    
    // Get the current client request data to pass to the utility function
    this.instance.model$.pipe(
      map(({ data }) => data),
      take(1)
    ).subscribe(clientRequest => {
      if (clientRequest) {
        openFieldGuideRequest(clientRequest as ClientRequestWithFieldGuide);
      }
    });
  }
}